import { FiSearch } from 'react-icons/fi';

export const matchingInformationData = {
  courseId: 'matching-information',
  courseType: 'reading-course',
  title: 'Matching Information (Paragraph Location)',
  icon: FiSearch,
  overview: {
    impact: 'Locate specific information inside a paragraph quickly and confidently',
    focus: 'Scanning, paraphrasing, paragraph mapping',
    uses: 'Reading section, especially academic texts with longer passages',
    duration: '25-30 minutes',
  },
  sections: [
    {
      title: 'What "Matching Information" Tests',
      content:
        '<p>Unlike <strong>Matching Headings</strong>, this task is not about the main idea. It is about finding a <strong>specific piece of information</strong> hidden somewhere in the passage and naming the paragraph that contains it.</p>',
      points: [
        'Each statement points to a detail (cause, example, definition, comparison, statistic, opinion).',
        'A paragraph may match more than one statement, or none at all.',
        'You will rarely find the exact words; expect a paraphrase.',
      ],
      warning:
        'Do not match by keyword alone. The paragraph must actually express the same idea, not just share vocabulary.',
    },
    {
      title: 'A Reliable Four-Step Strategy',
      content: '<p>Treat each statement like a clue, not a sentence to memorise:</p>',
      points: [
        'Identify the type of information first: a cause? an example? a contrast? a number?',
        'Underline 2-3 keywords you can paraphrase, not copy.',
        'Scan paragraph by paragraph for that idea, using topic sentences as anchors.',
        'Confirm by reading the surrounding 1-2 sentences before locking your answer.',
      ],
    },
    {
      title: 'Distractor Patterns to Watch',
      content: '<p>Wrong matches usually fall into one of these traps:</p>',
      table: {
        headers: ['Distractor Type', 'Why It Catches Test-Takers'],
        rows: [
          ['Same words, different idea', 'A keyword appears, but the paragraph says the opposite or speaks about a different aspect.'],
          ['Partial match', 'Half of the statement matches; the other half does not.'],
          ['Generalisation', 'Paragraph mentions a broad category; the statement names a very specific case (or vice versa).'],
          ['Wrong agent', 'Statement says "researchers found that ...", paragraph attributes it to "policymakers".'],
          ['Time mismatch', 'Statement describes the past; paragraph describes a current situation.'],
        ],
      },
    },
    {
      title: 'Order Does Not Help',
      content:
        '<p>Statements in this task are <strong>not in passage order</strong>. Do not assume the next answer is in the next paragraph.</p>',
      points: [
        'Mark statements you are unsure about and return to them after the easy ones.',
        'If a paragraph is rich in detail, expect more than one statement to land there.',
        'Quickly map each paragraph in 4-5 words before you start matching.',
      ],
    },
  ],
  exercises: [
    {
      title: 'Spot the Paraphrase',
      type: 'select',
      instructions: 'Choose the paragraph that matches the statement.',
      questions: [
        {
          text:
            'Statement: "An unexpected drawback was discovered after the technology was widely adopted."\nParagraphs:\nA. Initial laboratory tests showed strong, consistent results.\nB. Once millions of users joined, engineers noticed an issue with battery longevity that had not appeared in trials.\nC. Funding for the project tripled in two years.',
          options: ['A', 'B', 'C'],
          answer: 'B',
          explanation:
            'Paragraph B describes a problem found only after large-scale adoption — a paraphrase of "unexpected drawback discovered after wide use".',
        },
        {
          text:
            'Statement: "Researchers explained why the original assumption was incorrect."\nParagraphs:\nA. Earlier models predicted that consumption would fall every year.\nB. Newer studies revealed that the original calculations had ignored seasonal variations, leading to misleading conclusions.\nC. Many cities began publishing their own data dashboards.',
          options: ['A', 'B', 'C'],
          answer: 'B',
          explanation:
            'Paragraph B explicitly addresses why an earlier assumption was wrong, which matches the statement.',
        },
        {
          text:
            'Statement: "A specific example illustrated how regulation slowed adoption."\nParagraphs:\nA. New approval rules forced one major company to delay launch by 18 months in a single market.\nB. Most users were satisfied with the product.\nC. The team announced a long-term roadmap.',
          options: ['A', 'B', 'C'],
          answer: 'A',
          explanation:
            'Paragraph A gives a concrete case (one company, 18 months, one market) showing regulation as a brake, matching the statement.',
        },
      ],
    },
    {
      title: 'Same Words, Different Idea',
      type: 'select',
      instructions: 'Beware of paragraphs that share keywords but not meaning.',
      questions: [
        {
          text:
            'Statement: "The author argues that the policy succeeded in reducing waste."\nParagraphs:\nA. Critics claim the policy succeeded only on paper, since waste merely moved to neighbouring regions.\nB. After the policy was introduced, total recyclable waste fell by 22%, an outcome the author attributes to clearer rules.\nC. Waste collection routes were renumbered the following year.',
          options: ['A', 'B', 'C'],
          answer: 'B',
          explanation:
            'Paragraph B credits the author with a positive view of the result. Paragraph A also mentions success but in a critical voice — different idea.',
        },
        {
          text:
            'Statement: "Some experts disagreed with the approach but were ignored."\nParagraphs:\nA. Government advisers raised technical concerns, yet decisions continued without revision.\nB. Most experts welcomed the new approach because it shortened waiting lists.\nC. The approach was praised in international rankings.',
          options: ['A', 'B', 'C'],
          answer: 'A',
          explanation:
            'Paragraph A says advisers raised concerns and were not heeded — a paraphrase of "disagreed but were ignored".',
        },
      ],
    },
    {
      title: 'Find the Paragraph',
      type: 'fill-in',
      instructions: 'Type the letter (A, B, or C) of the paragraph that matches.',
      questions: [
        {
          text:
            'Statement: "A historical comparison illustrates how habits have changed."\nParagraphs:\nA. In 1990, fewer than five percent of households owned a personal computer; today the figure exceeds eighty percent.\nB. New devices appear on the market every year.\nC. Local repair shops are growing in popularity.\nWhich paragraph?',
          answer: 'A',
          explanation:
            'Paragraph A directly compares two periods (1990 vs today), illustrating a change in habits.',
        },
        {
          text:
            'Statement: "A solution proposed by the writer was later adopted by other countries."\nParagraphs:\nA. The writer recommended consolidating multiple agencies into a single authority. Three other countries followed this model within five years.\nB. The writer published a popular novel that year.\nC. Public surveys recorded mixed opinions on the issue.\nWhich paragraph?',
          answer: 'A',
          explanation:
            'Paragraph A states the writer\'s recommendation and notes other countries adopting it — matching both halves of the statement.',
        },
      ],
    },
    {
      title: 'Write Your Own Paraphrase',
      type: 'writing',
      instructions: 'Rewrite the statement in your own words before scanning.',
      questions: [
        {
          text:
            'Statement: "An unexpected benefit was reported only after long-term use."\nWrite a paraphrase you would scan for.',
          example:
            'A positive result that nobody anticipated, noticed only after years of use.',
          tip:
            'Replace "unexpected" with "surprising" or "not predicted"; replace "long-term use" with "after years of use".',
        },
        {
          text:
            'Statement: "Differences between rural and urban populations were narrowing."\nWrite a paraphrase you would scan for.',
          example:
            'The gap between people in cities and people in the countryside was getting smaller.',
          tip:
            'Look for words like "gap", "difference", "city vs country", "closing", "narrowing".',
        },
      ],
    },
  ],
  tips: [
    'Map each paragraph in 4-5 words before reading the statements.',
    'Statements are NOT in order — work whichever feels easiest first.',
    'A paragraph can match multiple statements; do not eliminate it after one use.',
    'Always read 1-2 sentences around the candidate sentence before locking the answer.',
  ],
  dailyPractice: [
    'Take a long article and write a 4-5 word summary for each paragraph.',
    'Practise paraphrasing 5 sentences a day using synonyms and structure changes.',
    'Time yourself: 90 seconds to find a single piece of information in a 5-paragraph article.',
    'Review wrong answers by classifying the trap (same words, partial match, generalisation, wrong agent, time mismatch).',
  ],
  backPath: '/exams/english/#reading',
  backLabel: 'Back to Reading',
  aiContext:
    'Matching Information (paragraph location): scanning for specific paraphrased details inside paragraphs without relying on word-for-word matches; recognising distractor patterns; mapping paragraphs quickly.',
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...matchingInformationData,
      title: matchingInformationData.title + ' (Foundations)',
      overview: { ...matchingInformationData.overview, duration: '15-20 minutes' },
      sections: matchingInformationData.sections.slice(0, 2),
      exercises: [
        {
          ...matchingInformationData.exercises[0],
          title: 'Spot the Paraphrase (Basic)',
          questions: matchingInformationData.exercises[0].questions.slice(0, 2),
        },
        {
          ...matchingInformationData.exercises[2],
          title: 'Find the Paragraph (Basic)',
          questions: matchingInformationData.exercises[2].questions.slice(0, 1),
        },
      ],
    };
  }
  if (difficulty >= 4) {
    return {
      ...matchingInformationData,
      title: matchingInformationData.title + ' (Advanced)',
      overview: { ...matchingInformationData.overview, duration: '30-35 minutes' },
      sections: [
        ...matchingInformationData.sections,
        {
          title: 'When Two Paragraphs Could Match',
          content:
            '<p>At higher levels, two paragraphs may both seem plausible. The right answer almost always <strong>fully expresses</strong> the statement, while the runner-up only matches <em>part</em> of it.</p>',
          points: [
            'Test the runner-up by asking: "Does it really say the cause/result/contrast described in the statement?"',
            'Reject paragraphs that share vocabulary but make a different claim.',
            'Prefer the paragraph where the statement\'s key relationship (cause-effect, contrast, comparison) is explicitly present.',
          ],
        },
      ],
      exercises: [
        ...matchingInformationData.exercises.map((ex) => ({
          ...ex,
          title: ex.title + ' (Advanced)',
        })),
        {
          title: 'Two-Candidate Discrimination',
          type: 'select',
          instructions:
            'Both paragraphs share keywords. Choose the one that fully expresses the statement.',
          questions: [
            {
              text:
                'Statement: "The author claims that reform is necessary despite short-term costs."\nA. Reform will create temporary disruptions, but the author argues these costs are justified by long-term benefits.\nB. Reform may create disruptions, and consumers should expect some short-term costs.\nWhich paragraph fully matches?',
              options: ['A', 'B'],
              answer: 'A',
              explanation:
                'Paragraph A both states the cost AND the author\'s claim that reform is justified. Paragraph B only mentions the cost.',
            },
          ],
        },
      ],
      tips: [
        ...(matchingInformationData.tips || []),
        'At Band 8+, justify your choice by explaining why the runner-up fails on at least one part of the statement.',
        'Prefer the paragraph that fully expresses the relationship in the statement, not just the keyword.',
      ],
    };
  }
  return matchingInformationData;
}
